import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { DataTable, LoadingState, ConfirmModal } from '../../../components/admin/shared';
import { Plus, Edit2, Trash2, Eye, EyeOff, Megaphone, CheckCircle } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get('/api/admin/announcements');
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Error loading announcements:', error);
      // Initialize with empty if API fails
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingAnnouncement.id) {
        await educentialApi.patch(`/api/admin/announcements/${editingAnnouncement.id}`, editingAnnouncement);
      } else {
        await educentialApi.post('/api/admin/announcements', editingAnnouncement);
      }

      setEditingAnnouncement(null);
      setSuccess(true);
      loadAnnouncements();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await educentialApi.delete(`/api/admin/announcements/${showDeleteModal.id}`);
      setShowDeleteModal(null);
      setSuccess(true);
      loadAnnouncements();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement. Please try again.');
    }
  };

  const handleToggleActive = async (announcementId, currentStatus) => {
    try {
      await educentialApi.patch(`/api/admin/announcements/${announcementId}`, {
        active: !currentStatus
      });
      loadAnnouncements();
    } catch (error) {
      console.error('Error toggling announcement:', error);
      alert('Failed to update announcement status.');
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (announcement) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{announcement.title}</div>
          <div className="text-xs text-gray-500">{announcement.targetRole || 'All Users'}</div>
        </div>
      )
    },
    {
      key: 'message',
      label: 'Message',
      render: (announcement) => (
        <div className="text-sm text-gray-700 truncate max-w-md">
          {announcement.message}
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      render: (announcement) => {
        const types = {
          info: 'bg-blue-100 text-blue-800',
          success: 'bg-green-100 text-green-800',
          warning: 'bg-yellow-100 text-yellow-800',
          error: 'bg-red-100 text-red-800'
        };
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${types[announcement.type] || types.info}`}>
            {announcement.type}
          </span>
        );
      }
    },
    {
      key: 'active',
      label: 'Status',
      render: (announcement) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          announcement.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {announcement.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (announcement) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(announcement.id, announcement.active);
            }}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
            title={announcement.active ? 'Deactivate' : 'Activate'}
          >
            {announcement.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingAnnouncement({ ...announcement });
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(announcement);
            }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (loading && !editingAnnouncement) {
    return (
      <AdminLayout title="Announcements">
        <LoadingState message="Loading announcements..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Announcements">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Announcements</h2>
            <p className="text-gray-600 mt-1">Create and manage platform-wide announcements</p>
          </div>
          <button
            onClick={() => setEditingAnnouncement({
              id: null,
              title: '',
              message: '',
              type: 'info',
              targetRole: '',
              active: true,
              startDate: '',
              endDate: ''
            })}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>New Announcement</span>
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-900 font-medium">Announcement saved successfully!</p>
          </div>
        )}

        {/* Edit Form */}
        {editingAnnouncement && (
          <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-500">
            <div className="flex items-center space-x-2 mb-4">
              <Megaphone className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAnnouncement.id ? 'Edit Announcement' : 'Create New Announcement'}
              </h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingAnnouncement.title}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Important announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingAnnouncement.message}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, message: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="Announcement message..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={editingAnnouncement.type}
                    onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="info">Information</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Alert</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <select
                    value={editingAnnouncement.targetRole}
                    onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, targetRole: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="">All Users</option>
                    <option value="STUDENT">Students Only</option>
                    <option value="EDUCATOR">Educators Only</option>
                    <option value="ADMIN">Admins Only</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editingAnnouncement.active}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active (visible to users)</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                onClick={() => setEditingAnnouncement(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!editingAnnouncement.title || !editingAnnouncement.message}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingAnnouncement.id ? 'Update' : 'Create'} Announcement
              </button>
            </div>
          </div>
        )}

        {/* Announcements Table */}
        <DataTable
          columns={columns}
          data={announcements}
          loading={loading}
          emptyMessage="No announcements found. Click 'New Announcement' to create one."
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal !== null}
          title="Delete Announcement?"
          message={`Are you sure you want to delete "${showDeleteModal?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          dangerous={true}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(null)}
        />
      </div>
    </AdminLayout>
  );
};

export default Announcements;
